<template>
  <Modal :open="isOpen" size="medium" @close="closeModal">
    <h1 class="text-gray-900 font-bold mb-4">Enregistrer un nouveau produit à votre formule</h1>

    <VeeForm v-slot="{ handleSubmit }" :validation-schema="schema" as="div">
      <form class="space-y-4" @submit="handleSubmit($event, handleAddProductItem)" ref="AddItemForm">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
          <div>
            <label for="itemTitle" class="block text-sm text-gray-700">
              Nom du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemTitle"
                name="itemTitle"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Nom du produit"
                v-model="itemTitle"
              />
              <ErrorMessage name="itemTitle" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemPrice" class="block text-sm text-gray-700">
              Prix du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2 relative">
              <Field
                id="itemPrice"
                name="itemPrice"
                type="text"
                class="pr-16 shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="249,99"
                v-model="itemPriceFormatted"
              />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span class="text-gray-500 sm:text-sm" id="price-currency">EUR</span>
              </div>
              <ErrorMessage name="itemPrice" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemType" class="block text-sm text-gray-700">
              Type du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                as="select"
                id="itemType"
                name="itemType"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                v-model="itemType"
              >
                <option disabled value="">Veuillez sélectionner un type</option>
                <option v-for="type in itemTypes(plan)" :key="type" :value="type">{{ $t(type) }}</option>
              </Field>
              <ErrorMessage name="itemType" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemReferenceId" class="block text-sm text-gray-700">
              Numéro de série ou IMEI
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemReferenceId"
                name="itemReferenceId"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="35478405412998702"
                v-model="itemReferenceId"
              />
              <ErrorMessage name="itemReferenceId" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemVendor" class="block text-sm text-gray-700">
              Vendeur / Marque
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemVendor"
                name="itemVendor"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Ex : Samsung, Apple, Sony..."
                v-model="itemVendor"
              />
              <ErrorMessage name="itemVendor" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemModel" class="block text-sm text-gray-700">
              Modèle
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemModel"
                name="itemModel"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Ex : Series 6, Q80T, Hero9, PlayStation 5..."
                v-model="itemModel"
              />
              <ErrorMessage name="itemModel" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemPurchaseDate" class="block text-sm text-gray-700">
              Date d'achat
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2 relative">
              <Field
                type="date"
                name="itemPurchaseDate"
                id="itemPurchaseDate"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                v-model="itemPurchaseDate"
              />
              <ErrorMessage name="itemPurchaseDate" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
        </div>

        <ProductItemPhotosField
          :form-data="photosFormData"
          :min-photos="MIN_PHOTOS"
          :error="photosError"
          @files="handlePhotosChange"
        />

        <ProductPhotosCheckResult :checking="checking" :result="checkResult" />

        <div class="mt-6">
          <div class="flex flex-row gap-4">
            <div class="sm:w-1/2">
              <button
                class="w-full border border-transparent px-4 py-2 text-lg font-medium text-button-font-primary bg-primary uppercase transition duration-300 transform hover:scale-105 rounded-lg focus:outline-hidden disabled:opacity-50 disabled:transform-none"
                type="submit"
                :disabled="checking || submitting"
                data-test="submit-product-item"
              >
                {{ photosNonCompliant ? t("register_anyway") : $t("Ajouter") }}
              </button>
            </div>
            <div class="sm:w-1/2">
              <button
                class="w-full border border-transparent bg-red-100 px-4 py-2 text-lg font-medium text-red-700 transition duration-300 transform hover:scale-105 rounded-lg focus:outline-hidden"
                type="button"
                @click="closeModal"
              >
                {{ $t("Close") }}
              </button>
            </div>
          </div>
        </div>
      </form>
    </VeeForm>
  </Modal>
</template>

<script>
import { Modal } from "@estaly/ui";
import { mapActions, mapGetters } from "vuex";
import { useI18n } from "vue-i18n";
import { ErrorMessage, Field, Form as VeeForm } from "vee-validate";
import * as yup from "yup";
import ProductItemPhotosField from "@/views/plan/ProductItemPhotosField.vue";
import ProductPhotosCheckResult from "@/views/plan/ProductPhotosCheckResult.vue";

// Mirrors the backend floor: fewer photos cannot cover every side of a product.
const MIN_PHOTOS = 3;

export default {
  components: {
    Modal,
    ErrorMessage,
    Field,
    VeeForm,
    ProductItemPhotosField,
    ProductPhotosCheckResult,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: ["isOpen", "plan"],

  data: () => {
    const schema = yup.object({
      itemTitle: yup.string().required("Le nom du produit est requis"),
      itemReferenceId: yup.string().required("Le numéro de série est requis"),
      itemVendor: yup.string().required("La marque du produit est requise"),
      itemModel: yup.string().required("Le modèle du produit est requis"),
      itemPurchaseDate: yup
        .date()
        .nullable()
        .transform((value, originalValue) => (originalValue === "" ? null : value))
        .max(new Date(), "La date d'achat ne peut pas être dans le futur")
        .min(
          new Date(new Date().setFullYear(new Date().getFullYear() - 3)),
          "La date d'achat ne peut pas être supérieure à 3 ans",
        )
        .required("La date d'achat est requise")
        .typeError("La date d'achat est invalide"),
      itemPrice: yup
        .string()
        .required("Le prix du produit est requis.")
        .matches(/^\d+(\.\d{1,2})?$|^\d+(,\d{1,2})?$/, "Le format du prix est invalide."),
      itemType: yup.string().required("Le type du produit est requis"),
    });
    return {
      MIN_PHOTOS,
      schema,
      addSuccess: false,
      itemTitle: "",
      itemPrice: "",
      itemType: "",
      itemReferenceId: "",
      itemVendor: "",
      itemModel: "",
      itemPurchaseDate: "",
      // FileField mutates this FormData in place under the `item_images[]` key.
      photosFormData: new FormData(),
      photoCount: 0,
      photosError: null,
      checking: false,
      submitting: false,
      checkResult: null,
      // Bumped on every invalidation: a check answering after one is stale and ignored.
      photosCheckVersion: 0,
    };
  },

  computed: {
    photosNonCompliant() {
      return this.checkResult?.verdict === "FAIL";
    },
    ...mapGetters("planSales", ["itemTypes"]),
    itemPriceFormatted: {
      get() {
        return this.itemPrice === undefined ? "" : this.itemPrice.toString().replace(".", ",");
      },
      set(value) {
        this.itemPrice = value.replace(",", ".");
      },
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),
    ...mapActions("plan", ["checkProductItemPhotos", "addProductItem"]),

    closeModal() {
      this.$emit("closeAddProductItemModal", this.addSuccess);
      this.resetAttributes();
    },

    refreshPlanSale() {
      this.$emit("refreshPlanSale");
    },

    resetAttributes() {
      this.addSuccess = false;
      this.itemTitle = "";
      this.itemPrice = "";
      this.itemType = "";
      this.itemReferenceId = "";
      this.itemVendor = "";
      this.itemModel = "";
      this.itemPurchaseDate = "";
      this.photosFormData = new FormData();
      this.photoCount = 0;
      this.photosError = null;
      this.checking = false;
      this.invalidatePhotosCheck();
    },

    invalidatePhotosCheck() {
      this.photosCheckVersion += 1;
      this.checkResult = null;
    },

    // New photos invalidate the previous verdict: the next submission checks them again.
    handlePhotosChange(count) {
      this.photoCount = count;
      this.invalidatePhotosCheck();
    },

    // Two-step submission: the photos are checked (and uploaded) first, then the product is
    // created. Non-conforming photos keep the customer in the modal with a warning, and a
    // second submission registers the product anyway, without a new check.
    // Guarded synchronously: a double click must not submit the product twice.
    async handleAddProductItem() {
      if (this.submitting) return;

      this.submitting = true;
      try {
        await this.submitProductItem();
      } finally {
        this.submitting = false;
      }
    },

    async submitProductItem() {
      if (this.photoCount < MIN_PHOTOS) {
        this.photosError = this.t("min_photos_error");
        return;
      }
      this.photosError = null;

      const warningAcknowledged = this.photosNonCompliant;
      const check = warningAcknowledged ? this.checkResult : await this.runPhotosCheck();
      if (!check?.photosVerdictToken) return;
      if (check.verdict === "FAIL" && !warningAcknowledged) return;

      const success = await this.addProductItem({
        planSaleId: this.plan.id,
        params: {
          title: this.itemTitle,
          price: this.itemPrice,
          itemType: this.itemType,
          referenceId: this.itemReferenceId,
          vendor: this.itemVendor,
          model: this.itemModel,
          purchaseDate: this.itemPurchaseDate,
          itemImageSignedIds: check.signedIds,
          photosVerdictToken: check.photosVerdictToken,
        },
      });
      if (!success) return;

      this.notify({
        category: "simple",
        type: "success",
        title: this.t("added_success"),
      });
      this.addSuccess = true;
      this.closeModal();
      this.refreshPlanSale();
    },

    // Returns null when the facts, the photos or the modal changed while the check ran: its
    // token was minted for values that are no longer the ones about to be submitted.
    async runPhotosCheck() {
      const version = this.photosCheckVersion;
      this.checking = true;
      this.checkResult = null;

      this.photosFormData.set("title", this.itemTitle);
      this.photosFormData.set("vendor", this.itemVendor);
      this.photosFormData.set("model", this.itemModel);
      this.photosFormData.set("item_type", this.itemType);

      try {
        const result = await this.checkProductItemPhotos({
          planSaleId: this.plan.id,
          formData: this.photosFormData,
        });
        if (version !== this.photosCheckVersion) return null;

        this.checkResult = result;
        return result;
      } finally {
        this.checking = false;
      }
    },
  },

  watch: {
    // The verdict was computed against these facts: changing one requires a new check.
    itemTitle: "invalidatePhotosCheck",
    itemVendor: "invalidatePhotosCheck",
    itemModel: "invalidatePhotosCheck",
    itemType: "invalidatePhotosCheck",
  },
};
</script>

<i18n src="./AddProductItemModal.i18n.json"></i18n>
